import { type JanusCtx, type Row, ecId, handBack, insertRoll, itemField, record, scalar, upper } from './context.js';
import { findWeaponAmmo, planThrownWeapon, planWeaponAmmo } from '../actions/weapon-attack-inventory.js';
import { weaponAttackRollType } from '../actions/weapon-attack.js';
import { explicitWeaponAttackReaders, type ExplicitWeaponReaders } from '../actions/explicit-weapon-attack.js';
import { sourceItemType } from './charged-cast.js';
import { computedValue, encounterField, posCoord } from '../../shared/array-types.js';

/** Invocation-local binding; the inventory instance never becomes a shared catalog definition. */
export function objectWeaponAttackDefinition(template: Row, weapon: Row, throwing: boolean, read: ExplicitWeaponReaders): Row {
  return { ...template, ...weapon, items: { ...record(template.items), ...record(weapon.items) },
    item_layer_types: { ...record(template.item_layer_types), ...record(weapon.item_layer_types),
      roll_type: weaponAttackRollType({ throwWeapon: throwing }, weapon, read) },
    _ftObjectWeaponAttack: true, _ftObjectWeaponThrow: throwing };
}

/** Plan before damage, then append to the same ability verdict once the hit resolves. */
export function planObjectWeaponProjectile(ctx: JanusCtx, weapon: Row, caster: Row, target: Row):
  { weapon: Row; entries: ExplicitWeaponReaders['entries']; finish: () => void } | null {
  if (weapon._ftObjectWeaponAttack !== true) return null;
  const read: ExplicitWeaponReaders = { value: ctx.read.value, entries: ctx.read.entries,
    inventoryField: (row, key) => ctx.read.inventoryField?.(row, key), itemField,
    itemType: row => sourceItemType(ctx.read, row) ?? String(itemField(row, 'item_type') ?? ''),
    computedValue, resolveItem: id => ctx.read.definition(id) };
  const encounterId = upper(encounterField(ctx.encounter, 'encounter_id'));
  const statuses = (row: Row): string[] => ctx.read.statuses(row).map(status => upper(scalar(status.status_item_id)));
  const position = (row: Row, key: string): unknown => posCoord(row, key as 'x_pos' | 'y_pos' | 'z_pos');
  const throwing = weapon._ftObjectWeaponThrow === true;
  const usesAmmo = !throwing && ctx.read.has(weapon, 'uses_ammo_classification');
  const ammo = usesAmmo ? findWeaponAmmo(caster, weapon, read) : null;
  if (usesAmmo && !ammo) handBack('weapon:ranged-ammo-unavailable');
  const plan = throwing ? planThrownWeapon(weapon, target, ctx.encounter, ecId(caster), encounterId,
    read, position, statuses, ctx.read.uuid)
    : ammo ? planWeaponAmmo(ammo, target, ctx.encounter, encounterId, '', read, position, ctx.read.uuid, statuses) : null;
  if (!plan) return null;
  if ('reason' in plan) handBack(plan.reason);
  const projected = 'weapon' in plan && typeof plan.weapon === 'function' ? plan.weapon(weapon) as Row : weapon;
  return { weapon: projected, entries: explicitWeaponAttackReaders(projected, caster, read).entries, finish: () => {
    const change = plan.finish(true, ctx.roll);
    (ctx.weaponInventoryChanges ??= []).push(change);
    insertRoll(ctx, { roll_type_1: change.log_type, roll_item_id: change.inventory_id, by: caster, to: target });
  } };
}
